import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../../assets/usjp-logo__1_-removebg-preview.png";
import api from "../../services/api";
import AuthShell from "../../components/auth/AuthShell";
import {
  PhotoRequirementsPanel,
  ExamplePhotoCard,
  PhotoExampleModal,
  ViewPhotoExampleLink,
} from "../../components/common/PhotoGuidelines";
import {
  sendOtp as sendOtpRequest,
  verifyOtp as verifyOtpRequest,
  registerMember,
  previewMemberRegistration,
  checkMember,
} from "../../services/authService";
import PaymentMethod from "./PaymentMethod";

// =============================================
// All backend calls go through the shared axios instance (src/services/api.js)
// and the auth service (src/services/authService.js). withCredentials is enabled
// there so the Laravel session cookie carries the OTP-verified state across the
// send-otp → verify-otp → member-registration flow.
// =============================================

// Steps: "email" → "otp" → "details" → "summary" → "submitted"
//   details  = Tab 1, collects all registration information
//   summary  = Tab 2, read-only review before final submission

const TITLE_OPTIONS = ["Mr", "Mrs", "Ms", "Miss", "Dr", "Rev."];

// Accepted profile-photo MIME types (JPG/JPEG share image/jpeg).
const ALLOWED_PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];

// Seconds the "Resend OTP" button stays disabled after each send, so users
// can't hammer the send-otp endpoint.
const RESEND_COOLDOWN_SECONDS = 60;

// Persists the post-submission state (the "Application Submitted" popup)
// across a page refresh or a later revisit in the same tab — without this,
// reloading wipes all in-memory state and drops the user back on the blank
// step-1 form even though their registration already exists server-side.
// Only this late part of the flow is persisted; steps 1-3 (email/otp/
// details/summary) are still lost on refresh, matching the fact that
// nothing has been submitted to the backend yet at that point. Deliberately
// does NOT resume straight into the payment modal itself (see showPayment
// below) — resuming must always re-land on the checkpoint popup first.
const PENDING_REGISTRATION_KEY = "sfmis_student_registration_pending";

const loadPendingRegistration = () => {
  try {
    return JSON.parse(sessionStorage.getItem(PENDING_REGISTRATION_KEY) || "null");
  } catch {
    return null;
  }
};

const clearPendingRegistration = () => {
  try {
    sessionStorage.removeItem(PENDING_REGISTRATION_KEY);
  } catch {
    /* storage unavailable — nothing to clear */
  }
};

// Maps the backend validator's field names (from the /preview response's
// `errors` object) onto this form's field keys, so server-side messages land on
// the same inputs the client-side rules use.
const BACKEND_FIELD_MAP = {
  title: "title",
  initials: "initials",
  name_denoted_by_initials: "nameWithInitials",
  lastname: "lastName",
  member_gender_id: "memberGenderId",
  nic: "studentId",
  primary_phone: "primaryPhone",
  secondary_phone: "secondaryPhone",
  date_of_birth: "dob",
  address: "address",
  membership_type: "membershipType",
  club_id: "club",
  photo: "photo",
};

export default function StudentRegistration() {
  const navigate = useNavigate();
  // Email typed on the Home/Login page and forwarded directly here (Club
  // Registration is temporarily hidden from the public flow, so "Register"
  // and "no account" entry points go straight to this form instead of
  // through SelectRegistration), so the field below can be pre-filled (still
  // editable) instead of re-typed.
  const location = useLocation();

  // ── Step tracking ──────────────────────────
  // "email" | "otp" | "details" | "summary" | "submitted"
  // Resume straight into "submitted" if a prior submission is still pending
  // payment (see PENDING_REGISTRATION_KEY) — otherwise a refresh on the
  // payment screen would silently drop back to the blank step-1 form.
  // Arriving back from /select-registration ("Member Registration") with an
  // already OTP-verified email skips straight to the details tab — the OTP
  // session lives in the backend session cookie, not in this component.
  const [step, setStep] = useState(() =>
    loadPendingRegistration()
      ? "submitted"
      : location.state?.otpVerified && location.state?.email
        ? "details"
        : "email"
  );

  // ── Email & OTP ────────────────────────────
  const [email, setEmail] = useState(() => loadPendingRegistration()?.email || location.state?.email || "");
  const [emailError, setEmailError] = useState("");
  const [otpSending, setOtpSending] = useState(false);

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [otpVerifying, setOtpVerifying] = useState(false);
  // Set when the email may not be re-registered. Two blocking states:
  //   "account"     — a completed member account exists (account_exists) → log in.
  //   "in_progress" — a registration is already started but unpaid
  //                   (registration_in_progress) → resume it, don't start over.
  // `alreadyRegistered` toggles the popup; `blockReason` picks its message;
  // `pendingProcess` carries the in-progress record so we can route to resume.
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [blockReason, setBlockReason] = useState("account");
  const [pendingProcess, setPendingProcess] = useState(null);
  // True once `pendingProcess` came from the OTP-verified /verify-otp response
  // rather than the pre-OTP /api/member/check existence check — only then does
  // it carry the full submitted payload RegistrationStatus needs, so only then
  // is it safe to navigate straight there instead of sending another OTP.
  const [pendingProcessVerified, setPendingProcessVerified] = useState(false);
  // True once the user has clicked "Continue Registration" on the in-progress
  // popup. While true, a successful OTP verify skips the popup entirely and
  // goes straight to /registration-status — the user already told us their
  // intent, so there's no need to make them click "Continue Registration"
  // again after verifying. Reset whenever a fresh (non-resume) OTP send starts.
  const [resumingRegistration, setResumingRegistration] = useState(false);
  const [timer, setTimer] = useState(165);
  // Seconds left before "Resend OTP" can be clicked again (0 = enabled).
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef([]);

  // ── Clubs ──────────────────────────────────
  const [clubs, setClubs] = useState([]);
  const [clubsLoading, setClubsLoading] = useState(true);
  const [clubsError, setClubsError] = useState("");

  // ── Genders (category-driven; from GET /api/member-genders) ──
  const [genders, setGenders] = useState([]);

  // ── Form ───────────────────────────────────
  const [membershipType, setMembershipType] = useState("");
  const [selectedClub, setSelectedClub] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoError, setPhotoError] = useState("");
  const [showPhotoExample, setShowPhotoExample] = useState(false);
  // Ref to the hidden file input so we can reset its value on removal.
  // Without this, re-selecting the SAME file after removing it won't fire the
  // input's onChange (the value is unchanged), so the new preview never shows.
  const photoInputRef = useRef(null);
  const [form, setForm] = useState({
    title: "",
    initials: "",
    nameWithInitials: "", // Name denoted by initials
    lastName: "",
    memberGenderId: "",
    studentId: "", // optional — maps to the API `nic` field
    dob: "",
    primaryPhone: "",
    secondaryPhone: "",
    address: "",
  });
  const [submitting, setSubmitting] = useState(false);
  // True while the backend /preview validation is in flight (the "Next" step).
  const [validating, setValidating] = useState(false);
  // The `data` object returned by POST /member-registrations/preview: the
  // backend's NORMALIZED view of the applicant (e.g. person names title-cased,
  // whitespace trimmed) plus the resolved payable_amount. The Review step
  // renders from this — not the raw typed form — so what the user confirms is
  // exactly what will be stored. Null until the first successful preview.
  const [previewData, setPreviewData] = useState(null);
  const [submitError, setSubmitError] = useState("");
  // The member created by a successful registration (carries the numeric id +
  // display name), and whether the "select payment method" popup is open.
  const [submittedMember, setSubmittedMember] = useState(() => loadPendingRegistration()?.submittedMember ?? null);
  // Deliberately always starts closed, even when resuming a pending
  // registration — the payment modal opens a real (CyberExt) payment window,
  // so a returning visit to /student-registration (e.g. a stale tab, or
  // clicking "Register as Member" again days later) must land on the
  // "Application Submitted" checkpoint and require an explicit "Proceed to
  // Payment" click, not silently reopen a live payment screen on mount.
  const [showPayment, setShowPayment] = useState(false);

  // Keep PENDING_REGISTRATION_KEY in sync so a refresh on the "submitted"
  // popup or payment modal restores exactly where the user left off (minus
  // showPayment itself — see above, that's intentionally never persisted).
  useEffect(() => {
    if (step !== "submitted" || !submittedMember?.process_id) return;
    try {
      sessionStorage.setItem(
        PENDING_REGISTRATION_KEY,
        JSON.stringify({ submittedMember, email })
      );
    } catch {
      /* storage unavailable — refresh will just restart the flow */
    }
  }, [step, submittedMember, email]);
  // Per-field validation messages, keyed by the form field name (plus the
  // pseudo-fields "membershipType" and "club"). A non-empty value drives both
  // the red outline on the field and the inline message rendered beneath it.
  const [fieldErrors, setFieldErrors] = useState({});

  // ── Photo preview URL (memoized to avoid blob leak on every render) ──
  const photoPreviewUrl = useMemo(
    () => (photoFile ? URL.createObjectURL(photoFile) : null),
    [photoFile]
  );

  useEffect(() => {
    return () => {
      if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    };
  }, [photoPreviewUrl]);

  // ── Timer countdown ────────────────────────
  useEffect(() => {
    if (step !== "otp" || timer <= 0) return;
    const id = setInterval(() => setTimer((t) => t - 1), 1000);
    return () => clearInterval(id);
  }, [step, timer]);

  const minutes = String(Math.floor(timer / 60)).padStart(2, "0");
  const seconds = String(timer % 60).padStart(2, "0");

  // ── Resend cooldown countdown ──────────────
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  // ── Load clubs + genders once we reach the details tab ──
  useEffect(() => {
    if (step !== "details") return;
    // =============================================
    // API CALL — GET /api/clubs
    // Response: [{ id: 1, club_name: "Cricket Club", ... }, ...]
    // =============================================
    api
      .get("/api/clubs")
      .then((res) => {
        // Students may only register under an approved (Active) club, so
        // filter out any club that isn't currently Active before listing.
        const approvedClubs = (res.data || []).filter(
          (c) => c.club_status === "Active"
        );
        setClubs(approvedClubs);
        setClubsLoading(false);
      })
      .catch(() => {
        setClubsError("Failed to load clubs. Please refresh.");
        setClubsLoading(false);
      });

    // =============================================
    // API CALL — GET /api/member-genders
    // Response: [{ category_id, category_code, description }, ...]
    // =============================================
    api
      .get("/api/member-genders")
      .then((res) => setGenders(res.data))
      .catch(() => setGenders([]));
  }, [step]);

  // ── Handlers ───────────────────────────────

  // Send OTP — separated from resend to avoid double-reset.
  // Returns true on success so callers can decide whether to advance the step.
  const sendOtp = async () => {
    setOtpSending(true);
    // =============================================
    // API CALL — POST /send-otp   Body: { email }
    // =============================================
    try {
      await sendOtpRequest(email);
      return true;
    } catch (err) {
      return false;
    } finally {
      setOtpSending(false);
    }
  };

  const handleSendOtp = async () => {
    if (!email.includes("@")) {
      setEmailError("Enter a valid email address.");
      return;
    }
    setEmailError("");
    // A normal "Send OTP" click is always a fresh attempt, not a resume.
    setResumingRegistration(false);
    // Block re-registration BEFORE sending an OTP and send no code. Two cases:
    //   account_exists          → completed account, must log in instead.
    //   registration_in_progress → an application already exists but is unpaid;
    //                              they must resume it, not start a fresh one.
    // A submitted-but-unpaid email lives only in member_registration_process, so
    // account_exists is false while registration_in_progress is true — the old
    // code checked only account_exists and let these users register again.
    // (verify-otp's checks remain a backstop.)
    try {
      const check = await checkMember(email);
      if (check?.account_exists) {
        setBlockReason("account");
        setAlreadyRegistered(true);
        return;
      }
      if (check?.registration_in_progress) {
        setBlockReason("in_progress");
        setPendingProcess(check?.registration_process || null);
        setPendingProcessVerified(false);
        setAlreadyRegistered(true);
        return;
      }
    } catch {
      // If the check fails, fall through and let the normal flow proceed.
    }
    const ok = await sendOtp();
    if (!ok) {
      setEmailError("Failed to send OTP. Please try again.");
      return;
    }
    setTimer(165);
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    setOtp(["", "", "", "", "", ""]);
    setStep("otp");
  };

  const handleResendOtp = async () => {
    // Ignore clicks while the cooldown is still running or a send is in flight.
    if (resendCooldown > 0 || otpSending) return;
    setOtp(["", "", "", "", "", ""]);
    setOtpError("");
    const ok = await sendOtp();
    if (!ok) {
      setOtpError("Failed to resend OTP. Please try again.");
      return;
    }
    setTimer(165);
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0)
      inputRefs.current[index - 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    const newOtp = [...otp];
    pasted.split("").forEach((ch, i) => {
      newOtp[i] = ch;
    });
    setOtp(newOtp);
    inputRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const handleVerifyOtp = async () => {
    const code = otp.join("");
    if (code.length < 6) {
      setOtpError("Enter all 6 digits.");
      return;
    }
    setOtpError("");
    setOtpVerifying(true);
    // =============================================
    // API CALL — POST /verify-otp   Body: { email, otp: code }
    // =============================================
    try {
      const data = await verifyOtpRequest(email, code);
      // Backstop mirroring the pre-OTP guard: even if the check was skipped or
      // its result changed, an existing account or an in-progress application
      // must not be allowed to start a fresh registration.
      if (data?.account_exists) {
        setBlockReason("account");
        setAlreadyRegistered(true);
        return;
      }
      if (data?.registration_in_progress) {
        // If the user got here by clicking "Continue Registration" on the
        // popup, they've already declared their intent — skip showing the
        // popup again and go straight to the fully-populated summary page.
        if (resumingRegistration) {
          navigate("/registration-status", {
            state: { email, registration: data?.registration_process || null },
          });
          return;
        }
        setBlockReason("in_progress");
        setPendingProcess(data?.registration_process || null);
        setPendingProcessVerified(true);
        setAlreadyRegistered(true);
        return;
      }
      // Verified and free to register — let the user choose Member vs Club
      // first. "Member Registration" there returns here on the details step.
      navigate("/select-registration", { state: { email, otpVerified: true } });
    } catch (err) {
      setOtpError(
        err?.response?.data?.message || "Invalid OTP. Please try again."
      );
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    // Clear this field's error as soon as the user starts correcting it.
    setFieldErrors((fe) => (fe[name] ? { ...fe, [name]: "" } : fe));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // The accept="" attribute is only a picker hint — drag-drop, the "All files"
    // option, or a renamed extension can still hand us a non-image. Validate the
    // real MIME type so only actual images are accepted.
    if (!ALLOWED_PHOTO_TYPES.includes(file.type)) {
      setPhotoError("Photo must be a JPG, PNG, or WebP image.");
      setPhotoFile(null);
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setPhotoError("Photo must be less than 2MB.");
      setPhotoFile(null);
      return;
    }
    setPhotoError("");
    setFieldErrors((fe) => (fe.photo ? { ...fe, photo: "" } : fe));
    setPhotoFile(file);
  };

  // Sri Lankan phone validation — accepts local (0XX…) or country-code
  // (+94… / 94…) formats, matching the backend rules. Spaces are ignored.
  //   primary   → must be a mobile: optional 0/94/+94 prefix + 7 + 8 digits
  //   secondary → any 9-digit local number with an optional 0/94/+94 prefix
  const isValidPrimaryPhone = (phone) =>
    /^(?:\+94|94|0)?7\d{8}$/.test(phone.replace(/\s/g, ""));
  const isValidSecondaryPhone = (phone) =>
    /^(?:\+94|94|0)?\d{9}$/.test(phone.replace(/\s/g, ""));

  // Initials must be single letters each followed by a dot, e.g. "T.N." or
  // "A.B.C." (spaces between them are tolerated). Rejects "TN", "T.N", "Tharindu".
  const isValidInitials = (value) =>
    /^([A-Za-z]\.\s?)+$/.test(value.trim());

  // NIC (National Identity Card) — matches the backend rule: the old format is
  // 9 digits followed by V/X, the new format is 12 digits.
  const isValidNIC = (value) =>
    /^(?:\d{9}[vVxX]|\d{12})$/.test(value.trim());

  // Validate everything collected on Tab 1. Returns a map of
  // field-name → message (empty map means valid). The photo lives outside
  // `form`, so its message is tracked under the "photo" key and mirrored into
  // `photoError` so it renders in the photo block.
  const validateDetails = () => {
    const errs = {};
    if (!photoFile) errs.photo = "Please upload your profile photo.";
    if (!form.title) errs.title = "Please select a title.";
    if (!form.initials.trim()) errs.initials = "Please enter your initials.";
    else if (!isValidInitials(form.initials))
      errs.initials = "Use a dot after each initial, e.g. T.N. or A.B.C.";
    if (!form.nameWithInitials.trim())
      errs.nameWithInitials = "Please enter the name denoted by your initials.";
    if (!form.lastName.trim()) errs.lastName = "Please enter your last name.";
    if (!form.memberGenderId) errs.memberGenderId = "Please select your gender.";
    if (!form.studentId.trim())
      errs.studentId = "Please enter your NIC (or guardian's NIC).";
    else if (!isValidNIC(form.studentId))
      errs.studentId = "Enter a valid NIC — 9 digits + V/X (e.g. 200012345V) or 12 digits.";
    if (!form.primaryPhone.trim())
      errs.primaryPhone = "Please enter your primary phone.";
    else if (!isValidPrimaryPhone(form.primaryPhone))
      errs.primaryPhone = "Enter a valid phone number (e.g. 0712345678 or +94712345678).";
    if (form.secondaryPhone && !isValidSecondaryPhone(form.secondaryPhone))
      errs.secondaryPhone = "Enter a valid secondary phone number or leave it empty.";
    if (!form.dob) errs.dob = "Please enter your date of birth.";
    if (!form.address.trim()) errs.address = "Please enter your address.";
    if (!membershipType) errs.membershipType = "Please select a membership type.";
    if (membershipType === "club" && !selectedClub)
      errs.club = "Please select a club.";
    return errs;
  };

  // Push a validation result into state: field outlines/messages via
  // `fieldErrors`, the photo message via `photoError`, and a single summary
  // line above the action button. Returns true when the form is valid.
  const applyValidation = (errs) => {
    setFieldErrors(errs);
    setPhotoError(errs.photo || "");
    const valid = Object.keys(errs).length === 0;
    setSubmitError(valid ? "" : "Please correct the highlighted fields below.");
    return valid;
  };

  // Builds the multipart payload sent to both /preview (Next) and the final
  // /member-registrations submit, so the data validated is exactly what's stored.
  const buildRegistrationFormData = () => {
    const fd = new FormData();
    fd.append("email", email);
    fd.append("title", form.title);
    fd.append("initials", form.initials);
    fd.append("nameWithInitials", form.nameWithInitials);
    fd.append("lastName", form.lastName);
    fd.append("memberGenderId", form.memberGenderId);
    // The "Student ID or Guardian ID" input maps to the API's `nic` field.
    if (form.studentId) fd.append("nic", form.studentId);
    if (form.dob) fd.append("dob", form.dob);
    // Strip spaces so a number typed as "+94 71 234 5678" matches the
    // backend's no-space phone regex.
    fd.append("primaryPhone", form.primaryPhone.replace(/\s/g, ""));
    if (form.secondaryPhone) fd.append("secondaryPhone", form.secondaryPhone.replace(/\s/g, ""));
    fd.append("address", form.address);
    fd.append("membershipType", membershipType);
    if (membershipType === "club") fd.append("clubId", selectedClub);
    fd.append("photo", photoFile);
    return fd;
  };

  // Pushes a backend 422 `errors` map onto the form: known fields get their
  // outline/inline message via `fieldErrors` (photo mirrored into `photoError`),
  // and anything without a matching field (e.g. the email uniqueness rule) is
  // surfaced in the summary line above the action button.
  const applyBackendErrors = (errors, fallback) => {
    const mapped = {};
    const unmapped = [];
    Object.entries(errors || {}).forEach(([key, msgs]) => {
      const msg = Array.isArray(msgs) ? msgs[0] : String(msgs);
      const field = BACKEND_FIELD_MAP[key];
      if (field) mapped[field] = msg;
      else unmapped.push(msg);
    });
    setFieldErrors((prev) => ({ ...prev, ...mapped }));
    if (mapped.photo) setPhotoError(mapped.photo);
    setSubmitError(unmapped[0] || fallback || "Please correct the highlighted fields below.");
  };

  // Tab 1 → Tab 2. Block on client-side validation AND backend /preview
  // validation, so the summary only ever shows data the backend will accept.
  const handleNext = async () => {
    if (!applyValidation(validateDetails())) return;
    if (validating) return;
    setSubmitError("");
    setValidating(true);
    try {
      // Keep the backend's normalized `data` so the Review step renders from it
      // instead of the raw typed values. Response shape: { valid, message, data }.
      const preview = await previewMemberRegistration(buildRegistrationFormData());
      setPreviewData(preview?.data || null);
      setStep("summary");
    } catch (err) {
      const data = err?.response?.data;
      if (data?.errors) applyBackendErrors(data.errors, data.message);
      else setSubmitError(data?.message || "We couldn't validate your details. Please try again.");
    } finally {
      setValidating(false);
    }
  };

  const selectedGender = genders.find(
    (g) => String(g.category_id) === String(form.memberGenderId)
  );
  const selectedClubName = clubs.find(
    (c) => String(c.id) === String(selectedClub)
  )?.club_name;

  const handleSubmit = async () => {
    // Final guard — the user can only reach here via a validated Tab 1,
    // but re-check in case state was edited.
    const errs = validateDetails();
    if (!applyValidation(errs)) {
      setStep("details");
      return;
    }

    // =============================================
    // API CALL — POST /api/member-registrations (multipart/form-data).
    // The backend (MemberRegistrationController) accepts camelCase aliases:
    // title, initials, nameWithInitials, lastName, memberGenderId, nic,
    // primaryPhone, secondaryPhone, dob, address,
    // membershipType (club|club_student|independent), clubId, photo.
    // =============================================
    const fd = buildRegistrationFormData();

    setSubmitError("");
    setSubmitting(true);
    try {
      // Registration now creates a PROCESS (not a member yet) and returns
      // { process_id, payable_amount, payment_reference, payment_payload, ... }
      // wrapped in an array. Peel any array nesting and carry the process
      // details forward — including the real gateway payment_payload, used
      // verbatim by PaymentMethod/cyberGatewayService — so PaymentMethod can
      // pay against the process.
      let data = await registerMember(fd);
      while (Array.isArray(data)) data = data[0];
      setSubmittedMember({
        process_id: data?.process_id,
        payable_amount: data?.payable_amount,
        payment_reference: data?.payment_reference,
        payment_payload: data?.payment_payload,
        email,
        initials: form.initials,
        name_denoted_by_initials: form.nameWithInitials,
        lastname: form.lastName,
      });
      setStep("submitted");
    } catch (err) {
      const data = err?.response?.data;
      const firstError = data?.errors
        ? Object.values(data.errors)[0]?.[0]
        : null;
      setSubmitError(
        firstError || data?.message || "Registration failed. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ── Top navigation bar — logo + branding only (matches the public site) ──
  const NavBar = () => (
    <nav
      className="fixed top-0 left-0 right-0 z-50"
      style={{ backgroundColor: "rgba(10,20,50,0.97)", backdropFilter: "blur(8px)" }}
    >
      <div className="max-w-[114.2857rem] mx-auto px-8 sm:px-12 lg:px-16 flex items-center h-20">
        {/* Logo + branding */}
        <button className="flex items-center gap-2 sm:gap-3 min-w-0" onClick={() => navigate("/")}>
          <img src={logo} alt="USJ" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
          <div className="text-left min-w-0">
            <p className="text-white font-bold text-[0.7857rem] sm:text-lg leading-tight truncate">University of Sri Jayewardenepura</p>
            <p className="text-[0.7143rem] sm:text-base leading-tight truncate" style={{ color: "#e8a020" }}>Physical Education Unit</p>
          </div>
        </button>
      </div>
    </nav>
  );

  // Footer copyright shared by every step (matches the login page).
  const Footer = () => (
    <div className="text-center mt-8">
      <button
        onClick={() => navigate("/")}
        className="text-blue-600 font-semibold hover:underline text-lg"
      >
        ← Back to Home
      </button>
      <p className="text-base text-gray-500 mt-3">
        © {new Date().getFullYear()} University of Sri Jayewardenepura
      </p>
    </div>
  );

  // Shared branding block for the email/OTP gate cards (matches the login page
  // and the club registration gate — keeps both pages visually identical).
  const GateHeader = () => (
    <div className="flex items-center gap-3 sm:gap-5 mb-6 sm:mb-10">
      <img src={logo} alt="USJ Logo" className="w-16 h-16 sm:w-24 sm:h-24 object-contain flex-shrink-0" />
      <div>
        <p className="font-semibold text-gray-800 text-sm sm:text-xl leading-tight whitespace-nowrap">University of Sri Jayewardenepura</p>
        <p className="text-sm sm:text-lg text-blue-600">Sports Facility Portal</p>
      </div>
    </div>
  );

  // Dismiss the "already registered" popup and return to the email step so the
  // user can enter a different address.
  const closeAlreadyRegistered = () => {
    setAlreadyRegistered(false);
    setBlockReason("account");
    setPendingProcess(null);
    setPendingProcessVerified(false);
    setResumingRegistration(false);
    setStep("email");
    setEmail("");
    setEmailError("");
  };

  // ══════════════════════════════════════════
  // ALREADY REGISTERED — popup (blocks re-registration)
  // Shown before any OTP is sent (and again as a verify-otp backstop) when the
  // typed email can't start a new registration. Two variants driven by
  // `blockReason`: a completed account ("account") vs. an unpaid application
  // still in progress ("in_progress"). It overlays the current step instead of
  // navigating away, so the user stays put.
  // ══════════════════════════════════════════
  const AlreadyRegisteredModal = () => {
    const inProgress = blockReason === "in_progress";
    const title = inProgress ? "Registration Already In Progress" : "Email Already Registered";
    const subtitle = inProgress ? "A registration was already started for" : "An account already exists for";
    const message = inProgress
      ? "You've already started registering with this email but haven't completed it. Please continue your existing registration instead of starting over."
      : "Please log in instead, or use a different email address to register.";
    const actionLabel = inProgress ? "Continue Registration" : "Log In";
    // "Continue Registration" must NOT jump straight to /registration-status
    // using a `pendingProcess` that came from the pre-OTP /api/member/check
    // existence check — that response doesn't carry the full submitted payload
    // (unlike /verify-otp's registration_process, which RegistrationStatus is
    // built to render). So we send the OTP and drop into the OTP step instead,
    // flagging `resumingRegistration` so handleVerifyOtp knows to skip this
    // popup on success and navigate straight to the summary page once it has
    // the fully-populated /verify-otp payload. `pendingProcessVerified` is a
    // defensive fallback for the rare case verify-otp reports in-progress
    // without that flag set (e.g. state was reached some other way).
    const onAction = inProgress
      ? pendingProcessVerified
        ? () =>
            navigate("/registration-status", {
              state: { email, registration: pendingProcess },
            })
        : async () => {
            setAlreadyRegistered(false);
            setResumingRegistration(true);
            const ok = await sendOtp();
            if (ok) {
              setTimer(165);
              setResendCooldown(RESEND_COOLDOWN_SECONDS);
              setOtp(["", "", "", "", "", ""]);
              setStep("otp");
            } else {
              setEmailError("Failed to send OTP. Please try again.");
            }
          }
      : () => navigate("/login", { state: { email } });
    return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="already-registered-title"
      onClick={closeAlreadyRegistered}
    >
      <div
        className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={closeAlreadyRegistered}
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-amber-50">
          <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 id="already-registered-title" className="text-xl font-bold text-gray-900 mb-1">{title}</h2>
        <p className="text-sm text-gray-500 mb-1">{subtitle}</p>
        <p className="text-sm font-semibold text-blue-700 mb-5 break-all">{email}</p>
        <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-4 mb-5">
          <p className="text-base font-semibold text-amber-700">
            {message}
          </p>
        </div>
        <button
          onClick={onAction}
          disabled={inProgress && otpSending}
          className="w-full bg-blue-700 hover:bg-blue-800 disabled:bg-blue-300 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg text-sm transition"
        >
          {inProgress && otpSending ? "Sending OTP…" : actionLabel}
        </button>
      </div>
    </div>
    );
  };

  // ══════════════════════════════════════════
  // STEP 1 — EMAIL
  // ══════════════════════════════════════════
  if (step === "email") {
    return (
      <AuthShell>
        <div className="w-full max-w-2xl">
          <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 lg:p-12 w-full">
            <GateHeader />

            <h1 className="text-3xl font-bold text-gray-900 mb-2 text-center">
              Registration
            </h1>
            <p className="text-lg text-gray-500 mb-8 text-center">
              Enter your email to verify your identity before registering.
            </p>

            <label className="block text-lg font-medium text-gray-700 mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError("");
              }}
              placeholder="example@sjp.ac.lk"
              className="w-full border border-gray-300 rounded-xl px-4 py-4 text-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-1"
            />
            {emailError && (
              <p className="text-base text-red-500 mb-2">{emailError}</p>
            )}

            <button
              onClick={handleSendOtp}
              disabled={otpSending || !email}
              className="w-full bg-blue-700 hover:bg-blue-800 disabled:bg-blue-300 text-white font-semibold py-5 rounded-xl transition mt-4 text-lg"
            >
              {otpSending ? "Sending OTP…" : "Send OTP"}
            </button>

            <p className="flex items-center justify-center gap-1.5 text-base text-gray-500 mt-4">
              <svg
                className="w-4 h-4 text-green-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Email verification required to register
            </p>
          </div>

          <div className="text-center mt-6">
            <button
              onClick={() => navigate("/")}
              className="text-blue-600 font-semibold hover:underline text-lg"
            >
              ← Back to Home
            </button>
          </div>
        </div>
        <Footer />
        {alreadyRegistered && <AlreadyRegisteredModal />}
      </AuthShell>
    );
  }

  // ══════════════════════════════════════════
  // STEP 2 — OTP VERIFY
  // ══════════════════════════════════════════
  if (step === "otp") {
    return (
      <AuthShell>
        <div className="w-full max-w-2xl">
          <div className="bg-white rounded-3xl shadow-xl p-12 w-full text-center">
            <GateHeader />

            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Verify Your Email
            </h2>
            <p className="text-lg text-gray-500 mb-1">
              Enter the 6 digit code sent to
            </p>
            <p className="text-lg font-semibold text-blue-700 mb-8">{email}</p>

            {/* OTP boxes */}
            <div
              className="flex justify-center gap-2 sm:gap-3 mb-4"
              onPaste={handleOtpPaste}
            >
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => (inputRefs.current[i] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  className="w-10 h-12 sm:w-14 sm:h-16 border-2 border-gray-300 rounded-lg text-center text-xl sm:text-2xl font-bold focus:outline-none focus:border-blue-600 transition"
                />
              ))}
            </div>

            {otpError && (
              <p className="text-base text-red-500 mb-2">{otpError}</p>
            )}

            <p className="text-base text-gray-500 mb-4">
              {timer > 0 ? (
                <>
                  Code expires in{" "}
                  <span className="font-semibold text-red-500">
                    {minutes}:{seconds}
                  </span>
                </>
              ) : (
                <span className="text-red-500 font-semibold">Code expired.</span>
              )}{" "}
              <button
                onClick={handleResendOtp}
                disabled={otpSending || resendCooldown > 0}
                className="text-blue-600 font-semibold hover:underline ml-1 disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
              >
                {otpSending
                  ? "Sending…"
                  : resendCooldown > 0
                  ? `Resend OTP in ${resendCooldown}s`
                  : "Resend OTP"}
              </button>
            </p>

            <button
              onClick={handleVerifyOtp}
              disabled={otpVerifying || otp.join("").length < 6}
              className="w-full bg-blue-700 hover:bg-blue-800 disabled:bg-blue-300 text-white font-semibold py-5 rounded-xl transition text-lg"
            >
              {otpVerifying ? "Verifying…" : "Verify & Continue"}
            </button>

            <button
              onClick={() => setStep("email")}
              className="mt-4 text-base text-gray-500 hover:text-blue-600 transition w-full"
            >
              Change email
            </button>
          </div>
        </div>
        <Footer />
        {alreadyRegistered && <AlreadyRegisteredModal />}
      </AuthShell>
    );
  }

  // The STEP 4 — SUBMITTED confirmation is no longer a separate page; it is
  // rendered as a popup over the summary (see the modal at the end of the main
  // return below).

  // ══════════════════════════════════════════
  // STEP 3 — REGISTRATION (Tab 1: details → Tab 2: summary)
  // ══════════════════════════════════════════
  const inputClass =
    "w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition";
  // Base (border/ring colours omitted) so the error/normal state can supply
  // them without Tailwind class-ordering ambiguity.
  const inputBase =
    "w-full rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 transition";
  // Field className that turns red when the named field has a validation error.
  const fieldClass = (name) =>
    `${inputBase} ${
      fieldErrors[name]
        ? "border border-red-500 focus:ring-red-500"
        : "border border-gray-300 focus:ring-blue-500"
    }`;
  // Inline error message rendered directly beneath a field.
  const fieldError = (name) =>
    fieldErrors[name] ? (
      <p className="text-xs text-red-500 mt-1">{fieldErrors[name]}</p>
    ) : null;
  const labelClass = "block text-sm font-medium text-gray-600 mb-1.5";
  const required = <span className="text-red-500">*</span>;

  // Shared card wrapper — generous padding + a hairline border so each section
  // reads as a distinct, breathable block instead of cramped rows.
  const cardClass =
    "bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-6 mb-5";

  // Consistent, visually-distinct section header: a blue accent bar + bold
  // title, an optional required marker, an optional helper subtitle, and a
  // hairline divider separating the header from its fields.
  const SectionHeading = ({ title, subtitle, req }) => (
    <div className="mb-5 pb-3 border-b border-gray-100">
      <div className="flex items-center gap-2.5">
        <span className="w-1 h-5 rounded-full bg-blue-600 flex-shrink-0" />
        <h2 className="text-[0.9375rem] font-bold text-gray-900">
          {title} {req && required}
        </h2>
      </div>
      {subtitle && (
        <p className="text-xs text-gray-400 mt-1.5 ml-3.5">{subtitle}</p>
      )}
    </div>
  );

  // Two-step tab indicator shared by both tabs.
  const TabSteps = () => (
    <div className="flex items-center gap-3 mb-4">
      {[
        { id: "details", n: 1, label: "Registration Details" },
        { id: "summary", n: 2, label: "Review & Submit" },
      ].map((t, i) => {
        const active = step === t.id;
        const done = t.id === "details" && step === "summary";
        return (
          <React.Fragment key={t.id}>
            <div className="flex items-center gap-2">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 flex-shrink-0 ${
                  done
                    ? "bg-green-500 text-white border-green-500"
                    : active
                    ? "bg-blue-700 text-white border-blue-700"
                    : "bg-gray-200 text-gray-500 border-gray-200"
                }`}
              >
                {done ? (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  t.n
                )}
              </div>
              <span
                className={`text-xs font-semibold ${
                  active || done ? "text-blue-700" : "text-gray-500"
                }`}
              >
                {t.label}
              </span>
            </div>
            {i === 0 && (
              <div
                className={`flex-1 h-px ${
                  step === "summary" ? "bg-blue-600" : "bg-gray-200"
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  // Rows rendered on the Tab 2 summary, in entry order. These render from the
  // backend preview's normalized `data` (previewData) so the Review step is a
  // faithful preview of what will be stored — falling back to the raw form
  // state only if the preview data is somehow missing. The backend returns
  // gender/club as IDs, so resolve them to labels the same way the form does.
  const p = previewData || {};
  const previewGenderName = previewData
    ? genders.find(
        (g) => String(g.category_id) === String(p.member_gender_id)
      )?.description
    : selectedGender?.description;
  const previewClubName = previewData
    ? clubs.find((c) => String(c.id) === String(p.club_id))?.club_name
    : selectedClubName;
  const previewIsClub =
    (previewData ? p.membership_type : membershipType) === "club" ||
    p.membership_type === "club_student";

  const summaryRows = [
    ["Title", previewData ? p.title : form.title],
    ["Initials", previewData ? p.initials : form.initials],
    [
      "Name Denoted by Initials",
      previewData ? p.name_denoted_by_initials : form.nameWithInitials,
    ],
    ["Last Name", previewData ? p.lastname : form.lastName],
    ["Gender", previewGenderName],
    ["Email Address", previewData ? p.email || email : email],
    [
      "Student NIC or Guardian NIC",
      (previewData ? p.nic : form.studentId) || "—",
    ],
    ["Date of Birth", (previewData ? p.date_of_birth : form.dob) || "—"],
    ["Primary Phone", previewData ? p.primary_phone : form.primaryPhone],
    [
      "Secondary Phone",
      (previewData ? p.secondary_phone : form.secondaryPhone) || "—",
    ],
    ["Address", previewData ? p.address : form.address],
    [
      "Membership Type",
      previewIsClub ? "Student Under Club" : "Independent User",
    ],
  ];
  if (previewIsClub) {
    summaryRows.push(["Club", previewClubName || "—"]);
  }
  if (p.payable_amount != null) {
    summaryRows.push(["Registration Fee", `Rs. ${p.payable_amount}`]);
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 m-0 px-4 sm:px-8 lg:px-12 pt-28 pb-8">
      <NavBar />
      <div className="max-w-[114.2857rem] mx-auto w-full">
        <TabSteps />

        {/* ─────────────── TAB 1 — REGISTRATION DETAILS ─────────────── */}
        {step === "details" && (
          <>
            {/* Profile Photo — at the top of the page */}
            <div className={cardClass}>
              <SectionHeading
                title="Profile Photo"
                req
                subtitle="Used for your QR membership card · Min 300×300px · Max 2 MB · PNG/JPG"
              />
              <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)_minmax(0,1fr)] gap-5">
                <div className="flex flex-row items-start gap-5">
                  <div
                    className={`w-28 h-32 rounded-xl overflow-hidden border-2 bg-gray-100 flex items-center justify-center flex-shrink-0 ${
                      photoError ? "border-red-500" : "border-gray-200"
                    }`}
                  >
                    {photoPreviewUrl ? (
                      <img
                        src={photoPreviewUrl}
                        alt="Preview"
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <svg
                        className="w-10 h-10 text-gray-300"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                    )}
                  </div>

                  <div className="flex flex-col items-start">
                    <label className="cursor-pointer">
                      <div className="px-6 py-2 rounded-lg border-2 border-gray-300 text-sm font-medium text-gray-700 hover:border-blue-500 hover:text-blue-600 transition text-center">
                        Upload Photo
                      </div>
                      <input
                        ref={photoInputRef}
                        type="file"
                        accept=".png,.jpg,.jpeg,.webp"
                        className="hidden"
                        onChange={handlePhotoChange}
                      />
                    </label>
                    <p className="text-xs text-gray-400 mt-2">
                      {photoFile ? photoFile.name : "No photo selected"}
                    </p>
                    <ViewPhotoExampleLink
                      className="mt-1"
                      onClick={() => setShowPhotoExample(true)}
                    />

                    {photoError && (
                      <p className="text-xs text-red-500 mt-2">{photoError}</p>
                    )}
                  </div>
                </div>

                <PhotoRequirementsPanel />
                <ExamplePhotoCard />
              </div>
            </div>
            <PhotoExampleModal open={showPhotoExample} onClose={() => setShowPhotoExample(false)} />

            {/* Personal Information — identity details */}
            <div className={cardClass}>
              <SectionHeading
                title="Personal Information"
                subtitle="Your name and identity details."
              />

              {/* 1 col on phones → 3 cols on tablet/desktop. Wide fields
                  span columns so each row stays full and aligned. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-5">
                {/* Row 1 — Title · Initials · Last Name */}
                <div>
                  <label className={labelClass}>Title {required}</label>
                  <select
                    name="title"
                    value={form.title}
                    onChange={handleFormChange}
                    className={`${fieldClass("title")} text-gray-700`}
                  >
                    <option value="">-- Select --</option>
                    {TITLE_OPTIONS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  {fieldError("title")}
                </div>

                <div>
                  <label className={labelClass}>Initials {required}</label>
                  <input
                    name="initials"
                    autoComplete="off"
                    value={form.initials}
                    onChange={handleFormChange}
                    placeholder="T.N."
                    className={fieldClass("initials")}
                  />
                  {fieldError("initials")}
                </div>

                <div>
                  <label className={labelClass}>Last Name {required}</label>
                  <input
                    name="lastName"
                    autoComplete="family-name"
                    value={form.lastName}
                    onChange={handleFormChange}
                    placeholder="Perera"
                    className={fieldClass("lastName")}
                  />
                  {fieldError("lastName")}
                </div>

                {/* Row 2 — Name Denoted by Initials (2 cols) · Gender */}
                <div className="md:col-span-2">
                  <label className={labelClass}>
                    Name Denoted by Initials {required}
                  </label>
                  <input
                    name="nameWithInitials"
                    value={form.nameWithInitials}
                    onChange={handleFormChange}
                    placeholder="Tharindu Nimesh"
                    className={fieldClass("nameWithInitials")}
                  />
                  {fieldError("nameWithInitials")}
                </div>

                <div>
                  <label className={labelClass}>Gender {required}</label>
                  <select
                    name="memberGenderId"
                    value={form.memberGenderId}
                    onChange={handleFormChange}
                    className={`${fieldClass("memberGenderId")} text-gray-700`}
                  >
                    <option value="">-- Select --</option>
                    {genders.map((g) => (
                      <option key={g.category_id} value={g.category_id}>
                        {g.description}
                      </option>
                    ))}
                  </select>
                  {fieldError("memberGenderId")}
                </div>

                {/* Row 3 — Student NIC · Date of Birth */}
                <div>
                  <label className={labelClass}>
                    Student NIC <span>or Guardian NIC</span> {required}
                  </label>
                  <input
                    name="studentId"
                    value={form.studentId}
                    onChange={handleFormChange}
                    placeholder="200012345678"
                    className={fieldClass("studentId")}
                  />
                  {fieldError("studentId")}
                </div>

                <div>
                  <label className={labelClass}>Date of Birth {required}</label>
                  <input
                    type="date"
                    name="dob"
                    value={form.dob}
                    onChange={handleFormChange}
                    max={new Date().toLocaleDateString("en-CA")}
                    className={`${fieldClass("dob")} text-gray-700`}
                  />
                  {fieldError("dob")}
                </div>
              </div>
            </div>

            {/* Contact Information — how we reach the member */}
            <div className={cardClass}>
              <SectionHeading
                title="Contact Information"
                subtitle="How we'll reach you."
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-5">
                {/* Row 1 — Email (2 cols) · Primary Phone */}
                <div className="md:col-span-2">
                  <label className={labelClass}>
                    Email Address {required}
                  </label>
                  <input
                    type="email"
                    value={email}
                    readOnly
                    className={`${inputClass} bg-gray-50 text-gray-500 cursor-not-allowed`}
                  />
                </div>

                <div>
                  <label className={labelClass}>Primary Phone {required}</label>
                  <input
                    name="primaryPhone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={form.primaryPhone}
                    onChange={handleFormChange}
                    placeholder="071 234 5678"
                    className={fieldClass("primaryPhone")}
                  />
                  {fieldError("primaryPhone")}
                </div>

                {/* Row 2 — Secondary Phone · Address (2 cols) */}
                <div>
                  <label className={labelClass}>Secondary Phone</label>
                  <input
                    name="secondaryPhone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={form.secondaryPhone}
                    onChange={handleFormChange}
                    placeholder="077 234 5678"
                    className={fieldClass("secondaryPhone")}
                  />
                  {fieldError("secondaryPhone")}
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>Address {required}</label>
                  <input
                    name="address"
                    autoComplete="street-address"
                    value={form.address}
                    onChange={handleFormChange}
                    placeholder="No. 1, Main Street, Colombo"
                    className={fieldClass("address")}
                  />
                  {fieldError("address")}
                </div>
              </div>
            </div>

            {/* Membership Details — type + (conditional) club selection */}
            <div className={cardClass}>
              <SectionHeading
                title="Membership Details"
                req
                subtitle="Choose how you'd like to join."
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label
                  className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition ${
                    membershipType === "club"
                      ? "border-blue-700 bg-blue-50"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="membershipType"
                    value="club"
                    checked={membershipType === "club"}
                    onChange={(e) => {
                      setMembershipType(e.target.value);
                      setFieldErrors((fe) => ({ ...fe, membershipType: "" }));
                    }}
                    className="mt-0.5 accent-blue-600"
                  />
                  <div>
                    <p className="font-semibold text-sm text-gray-800">
                      Student Under Club
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Register under a sports club. Requires payment and admin
                      approval.
                    </p>
                  </div>
                </label>

                <label
                  className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition ${
                    membershipType === "independent"
                      ? "border-blue-700 bg-blue-50"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="membershipType"
                    value="independent"
                    checked={membershipType === "independent"}
                    onChange={(e) => {
                      setMembershipType(e.target.value);
                      setSelectedClub("");
                      setFieldErrors((fe) => ({
                        ...fe,
                        membershipType: "",
                        club: "",
                      }));
                    }}
                    className="mt-0.5 accent-blue-600"
                  />
                  <div>
                    <p className="font-semibold text-sm text-gray-800">
                      Independent User
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Book facilities independently. Requires payment and admin
                      approval.
                    </p>
                  </div>
                </label>
              </div>
              {fieldError("membershipType")}

              {/* Club Selection — only when registering under a club */}
              {membershipType === "club" && (
                <div className="mt-5 pt-5 border-t border-gray-100">
                  <label className={labelClass}>
                    Select Your Club {required}
                  </label>
                  {clubsError ? (
                    <p className="text-xs text-red-500">{clubsError}</p>
                  ) : (
                    <select
                      value={selectedClub}
                      onChange={(e) => {
                        setSelectedClub(e.target.value);
                        setFieldErrors((fe) =>
                          fe.club ? { ...fe, club: "" } : fe
                        );
                      }}
                      disabled={clubsLoading}
                      className={`${fieldClass("club")} md:max-w-sm text-gray-700 disabled:bg-gray-50 disabled:text-gray-500`}
                    >
                      <option value="">
                        {clubsLoading ? "Loading clubs..." : "-- Select a Club --"}
                      </option>
                      {clubs.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.club_name}
                        </option>
                      ))}
                    </select>
                  )}
                  {fieldError("club")}
                  <p className="text-xs text-gray-500 mt-1.5">
                    Your registration will be reviewed by the club and admin.
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 mb-3">
              {submitError && (
                <p className="text-sm text-red-500 mr-auto">{submitError}</p>
              )}
              <button
                onClick={handleNext}
                disabled={validating}
                className="bg-blue-700 hover:bg-blue-800 disabled:bg-blue-300 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 rounded-lg text-sm transition"
              >
                {validating ? "Validating…" : "Next — Review"}
              </button>
            </div>
          </>
        )}

        {/* ─────────────── TAB 2 — REGISTRATION SUMMARY ─────────────── */}
        {(step === "summary" || step === "submitted") && (
          <>
            <div className="bg-white rounded-xl shadow-sm p-5 mb-4">
              <h2 className="font-bold text-sm mb-1 text-gray-900">
                Review Your Details
              </h2>
              <p className="text-xs text-gray-500 mb-5">
                Please confirm everything is correct before submitting.
              </p>

              <div className="flex flex-col sm:flex-row gap-6">
                {/* Photo */}
                <div className="flex-shrink-0 mx-auto sm:mx-0">
                  <div className="w-40 h-44 rounded-xl overflow-hidden border-2 border-gray-200 bg-gray-100 flex items-center justify-center">
                    {photoPreviewUrl ? (
                      <img
                        src={photoPreviewUrl}
                        alt="Profile"
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <span className="text-xs text-gray-300">No photo</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 text-center mt-2">
                    Profile Photo
                  </p>
                </div>

                {/* Field list */}
                <dl className="flex-1 divide-y divide-gray-100">
                  {summaryRows.map(([label, value]) => (
                    <div
                      key={label}
                      className="py-3 grid grid-cols-1 sm:grid-cols-3 gap-1"
                    >
                      <dt className="text-xs font-medium text-gray-500">
                        {label}
                      </dt>
                      <dd className="sm:col-span-2 text-sm text-gray-800 break-words">
                        {value || "—"}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mb-3">
              {submitError && (
                <p className="text-sm text-red-500 mr-auto">{submitError}</p>
              )}
              <button
                onClick={() => {
                  setSubmitError("");
                  setStep("details");
                }}
                disabled={submitting}
                className="font-semibold px-5 py-2 rounded-lg text-sm transition border-2 border-gray-300 text-gray-600 hover:border-gray-400 disabled:opacity-50"
              >
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-blue-700 hover:bg-blue-800 active:bg-blue-900 disabled:bg-blue-300 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 rounded-lg text-sm transition shadow-sm hover:shadow-md"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Submitting…
                  </span>
                ) : "Submit Registration"}
              </button>
            </div>
          </>
        )}

        <Footer />
      </div>

      {/* ══ STEP 4 — SUBMITTED (popup over the summary) ══ */}
      {step === "submitted" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div
            role="dialog"
            aria-modal="true"
            className="bg-white rounded-2xl shadow-2xl ring-1 ring-black/5 p-8 w-full max-w-md text-center"
          >
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-blue-50">
              <svg className="w-8 h-8 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Application Submitted!
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              Complete your payment to activate your registration.
            </p>

            <div className="bg-gray-50 rounded-xl p-4 mb-5 text-left space-y-3">
              {[
                { label: "Email verified", done: true, active: false },
                { label: "Registration form submitted", done: true, active: false },
                { label: "Payment pending", done: false, active: true },
              ].map((s, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      backgroundColor: s.done ? "#dcfce7" : s.active ? "#fef3c7" : "#f3f4f6",
                    }}
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      style={{ color: s.done ? "#16a34a" : s.active ? "#d97706" : "#9ca3af" }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={s.done ? 3 : 2}
                        d={
                          s.done
                            ? "M5 13l4 4L19 7"
                            : s.active
                            ? "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            : "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        }
                      />
                    </svg>
                  </div>
                  <span
                    className={`text-xs font-medium ${
                      s.done || s.active ? "text-gray-700" : "text-gray-500"
                    }`}
                  >
                    {s.label}
                  </span>
                </div>
              ))}
            </div>

            <button
              onClick={() => setShowPayment(true)}
              disabled={!submittedMember?.process_id}
              className="w-full bg-blue-700 hover:bg-blue-800 disabled:bg-blue-300 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg text-sm mb-3 transition"
            >
              Proceed to Payment
            </button>
            <button
              onClick={() => {
                // Leaving the flow entirely — a pending unpaid registration is
                // resumed via login/OTP → /registration-status instead, so
                // there's nothing left here to restore on a future refresh.
                clearPendingRegistration();
                navigate("/");
              }}
              className="w-full text-sm text-gray-500 hover:text-blue-600 transition"
            >
              Back to Login
            </button>
          </div>
        </div>
      )}

      {/* Select-payment-method popup — opened by "Proceed to Payment". On a
          successful payment we send the now-active member to the status page. */}
      {showPayment && submittedMember?.process_id && (
        <PaymentMethod
          member={submittedMember}
          email={email}
          onClose={() => setShowPayment(false)}
          onSuccess={() => {
            // Payment done. The member account is finalized server-side (once any
            // club verification clears); send them to login to sign in with OTP.
            clearPendingRegistration();
            navigate("/login", { state: { email } });
          }}
        />
      )}
    </div>
  );
}